import { el } from "../lib/dom.js";
import { emitExchange, emitSimple, type Exchange } from "../lib/exchange.js";
import { actionRow, choiceControl, field, type ScreenHost } from "./ui.js";

/** The words, exported so the tests pin them. */
export const TEXT_WORDS = {
  format: "Simple is a checklist for messages. Full keeps every field, for import.",
  snapshot: "A copy of your saved list, taken when you opened this. Reopen for newer changes.",
} as const;

export interface TextShare {
  node: HTMLFormElement;
  /** The checked format: where the focus starts. */
  focus(): HTMLElement;
  /** The surface has gone: a late answer changes nothing and says nothing. */
  dispose(): void;
}

/**
 * A text copy of a list or an item: Simple or Full, from one capture taken
 * when the dialog opened. The share dialog's Text tab (g73), and the whole of
 * an item's dialog. `close` puts Close before Share, in the one row both
 * dialogs end with (g75).
 */
export function textShare(
  host: Pick<ScreenHost, "announce">,
  snapshot: Exchange,
  single: boolean,
  options: { close?: () => void } = {},
): TextShare {
  let useShare = typeof navigator.share === "function";
  let pending = false;
  let alive = true;
  const format = choiceControl("Format", [{ value: "simple", label: "Simple" }, { value: "full", label: "Full" }], "simple");
  format.node.classList.add("share-format");
  format.node.setAttribute("aria-describedby", "share-format-note");
  const preview = el("textarea", { attrs: { id: "share-yaml", readonly: true, rows: 6, spellcheck: "false", "aria-describedby": "share-note" } });
  const include = el("input", { attrs: { id: "share-completed", type: "checkbox" } });
  const status = el("p", { class: "share-status popup-note" });
  const submit = el("button", { class: "share-submit", text: useShare ? "Share" : "Copy", attrs: { type: "submit" } });
  const close = options.close
    ? el("button", { class: "secondary share-close", text: "Close", attrs: { type: "button" }, on: { click: options.close } })
    : undefined;
  const paint = () => {
    if (pending) return;
    preview.value = format.value() === "simple"
      ? emitSimple(snapshot, single || include.checked)
      : emitExchange(snapshot, single || include.checked);
  };
  format.node.addEventListener("change", paint);
  include.addEventListener("change", paint);
  paint();

  const say = (message: string) => { status.textContent = message; host.announce(message); };
  const manual = () => {
    preview.focus(); preview.select();
    say("Automatic copying is unavailable. Copy the selected text manually.");
  };
  async function sendText(): Promise<void> {
    if (pending || !alive) return;
    const sharing = useShare;
    const text = preview.value;
    status.textContent = "";
    pending = true; submit.disabled = true; include.disabled = true; format.node.disabled = true;
    try {
      // No await before either browser call: it must retain this click's activation.
      if (sharing) await navigator.share({ text });
      else if (typeof navigator.clipboard?.writeText === "function") await navigator.clipboard.writeText(text);
      else { manual(); return; }
      if (alive) say(sharing ? "Passed to your share app." : "Copied to clipboard.");
    } catch (error) {
      if (!alive) return;
      if (sharing && typeof error === "object" && error !== null && "name" in error && error.name === "AbortError") return;
      if (sharing) {
        useShare = false; submit.textContent = "Copy";
        say("Sharing is unavailable. Use Copy or copy the preview manually.");
      } else manual();
    } finally {
      pending = false;
      if (alive) { submit.disabled = false; include.disabled = false; format.node.disabled = false; }
    }
  }
  // Two groups, each a control and what explains it (g75): the format, then
  // what is sent and when it was taken.
  const node = el("form", { class: "share-form", children: [
    el("div", { class: "form-group", children: [
      format.node,
      el("p", { class: "popup-note", attrs: { id: "share-format-note" }, text: TEXT_WORDS.format }),
    ] }),
    el("div", { class: "form-group", children: [
      !single && el("label", { class: "share-completed", children: [include, el("span", { text: "Include completed" })] }),
      field("share-yaml", "Text to share", preview),
      el("p", { class: "popup-note", attrs: { id: "share-note" }, text: TEXT_WORDS.snapshot }),
    ] }),
    status,
    actionRow([close], submit, "share-actions"),
  ], on: { submit: event => { event.preventDefault(); void sendText(); } } });
  return {
    node,
    focus: () => format.node.querySelector<HTMLInputElement>("input:checked")!,
    dispose: () => { alive = false; },
  };
}
